import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AuthResponse, MeResponse, Permission, User, AccountKind } from '@/types';
import { clearTokens, setSession } from '@/shared/lib/apiClient';
import { queryClient } from '@/shared/lib/queryClient';
import { STORAGE_KEYS } from '@/utils/constants';

interface AuthState {
  user: User | null;
  permissions: Permission[];
  sessionId: string | null;
  accountKind: AccountKind | null;
  postAuthRoute: string | null;
  requiresProfileSetup: boolean;
  isAuthenticated: boolean;
  isInitializing: boolean;
  setAuth: (payload: AuthResponse) => void;
  setMe: (payload: MeResponse) => void;
  updateUser: (user: User) => void;
  setInitializing: (value: boolean) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      permissions: [],
      sessionId: null,
      accountKind: null,
      postAuthRoute: null,
      requiresProfileSetup: false,
      isAuthenticated: false,
      isInitializing: true,

      setAuth: ({ user, tokens, sessionId, permissions, accountKind, postAuthRoute, requiresProfileSetup }) => {
        const previousUserId = get().user?.id;
        if (previousUserId && previousUserId !== user.id) {
          queryClient.clear();
        }
        setSession(tokens.accessToken, tokens.refreshToken, sessionId);
        set({
          user,
          permissions,
          sessionId,
          accountKind: accountKind ?? null,
          postAuthRoute: postAuthRoute ?? null,
          requiresProfileSetup: requiresProfileSetup ?? false,
          isAuthenticated: true,
          isInitializing: false,
        });
      },

      setMe: ({ user, permissions, accountKind, postAuthRoute, requiresProfileSetup }) => {
        const previousUserId = get().user?.id;
        if (previousUserId && previousUserId !== user.id) {
          queryClient.clear();
        }
        set({
          user,
          permissions,
          accountKind,
          postAuthRoute,
          requiresProfileSetup,
          isAuthenticated: true,
          isInitializing: false,
        });
      },

      updateUser: (user) => {
        set({ user });
      },

      setInitializing: (value) => {
        set({ isInitializing: value });
      },

      logout: () => {
        clearTokens();
        queryClient.clear();
        try {
          sessionStorage.removeItem(STORAGE_KEYS.AUTH_INTENT);
        } catch {
          // ignore storage errors
        }
        set({
          user: null,
          permissions: [],
          sessionId: null,
          accountKind: null,
          postAuthRoute: null,
          requiresProfileSetup: false,
          isAuthenticated: false,
          isInitializing: false,
        });
      },
    }),
    {
      name: 'travel-guide-auth',
      partialize: (state) => ({
        user: state.user,
        permissions: state.permissions,
        sessionId: state.sessionId,
        accountKind: state.accountKind,
        postAuthRoute: state.postAuthRoute,
        requiresProfileSetup: state.requiresProfileSetup,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
